import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Alert,
  Button,
  Flex,
  Form,
  Select,
  Tag,
  Typography
} from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ChecklistItem } from 'src/analytics'
import {
  organizationsAccessAddCreateMutation,
  organizationsOrganizationsRetrieveOptions,
  usersListInfiniteOptions
} from 'src/api/main'
import type {
  PaginatedUserListList,
  UserList,
  UsersListData
} from 'src/api/main'
import { InfiniteSelect, ResizableDrawer } from 'src/components'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { buildWorkspaceInviteUrl } from 'src/features/workspaces/utils/inviteUrl'
import { useShowApiErrors, type OptionTransformType } from 'src/utils'
import { isEmailValid } from 'src/utils/string'

import { workspaceUsersListQueryKey } from '../hooks'

const { Text, Paragraph } = Typography

type Props = {
  open: boolean
  workspaceId: number
  onClose: () => void
  /** Called after a successful invite (e.g. to refresh the table). */
  onSuccess?: () => void
}

type FormValues = {
  emails: string[]
  head_user: number | null
}

export const InviteUserDrawer = ({
  open,
  workspaceId,
  onClose,
  onSuccess
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()

  const { data: organization } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({ path: { id: workspaceId } }),
    enabled: open && Number.isFinite(workspaceId)
  })

  const reportStep = useReportJourneyStep(workspaceId)

  const { mutate: inviteUser, isPending } = useMutation({
    ...organizationsAccessAddCreateMutation(),
    onSuccess: (_data, variables) => {
      const count = variables.body.emails.length
      message.success(t('users.invite.success', { count }))
      form.resetFields()
      onSuccess?.()
      void queryClient.invalidateQueries({
        queryKey: workspaceUsersListQueryKey(workspaceId)
      })
      // Sending the invitations is what finishes the checklist's
      // `invite_users` step, so this is where the platform is told — not the
      // checklist row, which only opens this page.
      reportStep(ChecklistItem.INVITE)
      onClose()
    },
    onError: (error) => showApiErrors(error, form)
  })

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      emails: [],
      head_user: null
    })
  }, [open, form])

  const handleSubmit = (values: FormValues) => {
    if (!organization?.domain_name) {
      message.error(t('users.invite.error.workspaceNotReady'))
      return
    }
    const invalid = values.emails.filter((e) => !isEmailValid(e))
    if (invalid.length > 0) {
      form.setFields([
        {
          name: 'emails',
          errors: [
            t('users.invite.error.invalidEmails', {
              emails: invalid.join(', ')
            })
          ]
        }
      ])
      return
    }
    // No role travels with an invite: `OrganizationInviteUser` carries none,
    // and an invited person has no access record to assign one to yet. They
    // land on the workspace's default role and are re-assigned from the member
    // page (`UserRoleAssignForm`) once they accept.
    inviteUser({
      body: {
        organization: workspaceId,
        emails: values.emails,
        head_user: values.head_user ?? null,
        invite_url: buildWorkspaceInviteUrl(organization.domain_name)
      }
    })
  }

  return (
    <ResizableDrawer
      open={open}
      onClose={() => {
        if (!isPending) onClose()
      }}
      defaultWidth={520}
      storageKey="users-invite-drawer-width"
      title={t('users.invite.drawerTitle')}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={isPending}>
            {t('users.invite.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => form.submit()}
          >
            {t('users.invite.submit')}
          </Button>
        </Flex>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t('users.invite.description')}
      </Paragraph>

      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
        initialValues={{
          emails: [],
          head_user: null
        }}
      >
        <Form.Item
          name="emails"
          label={t('users.invite.fields.emails')}
          tooltip={t('users.invite.fields.emailsTooltip')}
          rules={[
            {
              required: true,
              validator: (_rule, value: string[]) => {
                if (!value || value.length === 0) {
                  return Promise.reject(
                    new Error(t('users.invite.error.noEmails'))
                  )
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <Select
            mode="tags"
            tokenSeparators={[',', ' ', ';', '\n', '\t']}
            placeholder={t('users.invite.fields.emailsPlaceholder')}
            tagRender={({ label, value, closable, onClose: removeTag }) => {
              const text = (label ?? value) as string
              const valid = isEmailValid(text)

              return (
                <Tag
                  closable={closable}
                  onClose={removeTag}
                  color={valid ? 'default' : 'error'}
                  style={{ marginInlineEnd: 4 }}
                >
                  {text}
                </Tag>
              )
            }}
            open={false}
            suffixIcon={null}
          />
        </Form.Item>

        <Form.Item
          name="head_user"
          label={t('users.invite.fields.manager')}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('users.invite.fields.managerHint')}
            </Text>
          }
        >
          <InfiniteSelect<UsersListData, PaginatedUserListList, UserList>
            allowClear
            getQueryFunction={usersListInfiniteOptions}
            baseOptions={{ query: { organization: workspaceId } }}
            getOptions={({ options }: OptionTransformType<UserList>) =>
              options.map((u) => ({
                value: u.id,
                label: `${u.first_name} ${u.last_name}`.trim() || u.email
              }))
            }
            placeholder={t('users.invite.fields.managerPlaceholder')}
          />
        </Form.Item>

        {!organization?.domain_name ? (
          <Alert
            type="warning"
            showIcon
            title={t('users.invite.error.workspaceNotReady')}
            style={{ marginTop: 12 }}
          />
        ) : null}
      </Form>
    </ResizableDrawer>
  )
}
